/**
 * Chart theming + per-material price formatting.
 *
 * lightweight-charts paints to a canvas, so it can't consume CSS classes —
 * we resolve the app's design tokens at chart-construction time via
 * getComputedStyle. Fallbacks keep jsdom (no real computed styles) and any
 * missing-token regression rendering sensibly dark.
 */

export interface ChartColors {
  background: string;
  text: string;
  grid: string;
  bull: string;
  bear: string;
  finbert: string;
}

/**
 * Normalize any CSS color the tokens may hold (the app uses oklch())
 * into a form lightweight-charts can parse (hex/rgb). The library's own
 * parser throws "Failed to parse color: oklch(…)" — it only speaks
 * hex/rgb/hsl/named — so feeding tokens through a canvas round-trip
 * (browsers re-serialize fillStyle to #rrggbb / rgba()) is mandatory,
 * not cosmetic. jsdom has no 2D context → fall back.
 */
function normalizeColor(raw: string, fallback: string): string {
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return fallback;
    ctx.fillStyle = fallback;
    ctx.fillStyle = raw;
    return typeof ctx.fillStyle === "string" ? ctx.fillStyle : fallback;
  } catch {
    return fallback;
  }
}

export function resolveChartColors(): ChartColors {
  const cs =
    typeof window !== "undefined" && typeof document !== "undefined"
      ? window.getComputedStyle(document.documentElement)
      : null;
  const v = (name: string, fallback: string): string => {
    const raw = cs?.getPropertyValue(name).trim();
    return raw ? normalizeColor(raw, fallback) : fallback;
  };
  return {
    background: v("--card", "#131313"),
    text: v("--muted-foreground", "#a3a3a3"),
    grid: v("--border", "#2a2a2a"),
    bull: v("--chart-bull", "#10b981"),
    bear: v("--chart-bear", "#ef4444"),
    finbert: v("--primary", "#38bdf8"),
  };
}

/**
 * Tick-size-aware display precision. A 2-decimal default broke FX pairs
 * (EURUSD moves in 0.00001 steps) — and JPY pairs / indexes sit between.
 */
const PRICE_PRECISION: Record<string, number> = {
  EURUSD: 5,
  GBPUSD: 5,
  USDJPY: 3,
  USDCHF: 5,
  AUDUSD: 5,
  USDCAD: 5,
  NZDUSD: 5,
  DXY: 3,
  XAUUSD: 2,
  WTI: 2,
  BRENT: 2,
  BTCUSD: 1,
};

export function pricePrecisionFor(materialId: string): number {
  return PRICE_PRECISION[materialId] ?? 2;
}
